import { BLOCKED_EMAIL_DOMAINS, GENERIC_DOMAINS } from '../config/constants.js'

/** Same-domain research at or above this can name the company. Below it, the domain stands. */
export const COMPANY_CONFIDENCE_THRESHOLD = 0.75

export interface CompanyCandidate {
  name?: string | null
  domain?: string | null
  orgNo?: string | null
  confidence?: number | null
}

export interface CompanyDecision {
  status: 'none' | 'bound'
  /** The plate name, or a name that belongs to the plate domain. Never a different entity. */
  companyName: string
}

export function emailDomain(email: string | null | undefined): string {
  return email?.split('@')[1]?.toLowerCase().trim().replace(/^www\./, '') ?? ''
}

/** Free inboxes and reserved domains. These never get a company lookup. */
export function isGenericOrFreeDomain(domain: string | null | undefined): boolean {
  const host = (domain ?? '').toLowerCase().replace(/^www\./, '').trim()
  if (!host || !host.includes('.')) return true
  if (GENERIC_DOMAINS?.has(host) || BLOCKED_EMAIL_DOMAINS?.has(host)) return true
  const stem = host.split('.')[0] ?? ''
  if (stem === 'example' || stem === 'test' || stem === 'localhost' || stem === 'invalid') return true
  if (
    host.endsWith('.example') ||
    host.endsWith('.test') ||
    host.endsWith('.invalid') ||
    host.endsWith('.localhost')
  ) {
    return true
  }
  return false
}

function compact(value: string): string {
  return value
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/[^a-z0-9]/g, '')
}

function nameAlignsWithDomain(name: string, domain: string): boolean {
  const stem = domain.split('.')[0] ?? ''
  const folded = compact(name)
  if (stem.length < 3 || folded.length < 3) return false
  return folded.includes(stem) || stem.includes(folded)
}

function domainsMatch(emailHost: string, candidateHost: string | null | undefined): boolean {
  const host = (candidateHost ?? '').toLowerCase().replace(/^www\./, '').trim()
  if (!host) return false
  return host === emailHost || host.endsWith(`.${emailHost}`) || emailHost.endsWith(`.${host}`)
}

/**
 * The company typed on the plate wins. Research may keep a name only when it
 * belongs to that email domain. A free or reserved domain is not looked up.
 * There is no confirmation question.
 */
export function decideCompanyBinding(input: {
  email?: string | null
  plateCompany?: string | null
  candidate?: CompanyCandidate | null
}): CompanyDecision {
  const plate = input.plateCompany?.trim() ?? ''
  if (plate.length >= 2) {
    return { status: 'bound', companyName: plate.slice(0, 120) }
  }

  const domain = emailDomain(input.email)
  if (!domain || isGenericOrFreeDomain(domain)) {
    return { status: 'none', companyName: '' }
  }

  const name = input.candidate?.name?.trim() ?? ''
  const candidateDomain = input.candidate?.domain?.trim() || ''
  if (candidateDomain && !domainsMatch(domain, candidateDomain)) {
    return { status: 'none', companyName: '' }
  }
  if (name.length >= 2 && nameAlignsWithDomain(name, domain)) {
    return { status: 'bound', companyName: name.slice(0, 120) }
  }

  const confidence = typeof input.candidate?.confidence === 'number' ? input.candidate.confidence : 0
  const sameHost = Boolean(candidateDomain) && domainsMatch(domain, candidateDomain)
  const orgNo = input.candidate?.orgNo?.replace(/\s/g, '') ?? ''
  if (sameHost && name.length >= 2 && (confidence >= COMPANY_CONFIDENCE_THRESHOLD || orgNo.length >= 9)) {
    return { status: 'bound', companyName: name.slice(0, 120) }
  }
  return { status: 'none', companyName: '' }
}

/**
 * Research names that do not belong to the visitor's company and must be replaced
 * by `companyLabel` (person.company, summary). Used by lead-research finish().
 */
export function rejectedResearchCompanyNames(input: {
  email: string
  plate: string
  companyLabel: string
  names: string[]
}): string[] {
  const unique = input.names
    .map(value => value.trim())
    .filter(
      (value, index, all) =>
        value.length >= 2 && value !== input.companyLabel && all.indexOf(value) === index,
    )
  // A company typed in the form is never replaced by a research or domain-derived name,
  // whatever the email domain (sess_ce7fcedb, «F.B/c Lab (farzadbayat.com)» over the typed firm).
  if (input.plate.trim().length >= 2) return unique
  return unique.filter(
    name => decideCompanyBinding({ email: input.email, candidate: { name, confidence: 0 } }).status !== 'bound',
  )
}
